import { Currency } from "./InvoiceTypes";
import { Chain } from "./WalletTypes";

export type MarketingData = {
  notifications_enabled: boolean;
  survey_displayed?: boolean;
  utm_source?: string;
  utm_medium?: string;
  utm_campaign?: string;
};

export enum AccountType {
  CONTRACTOR = 10,
  BUSINESS = 20,
}

export enum PhoneChannel {
  SMS = "sms",
  WHATSAPP = "whatsapp",
}

export enum KycProvider {
  SUMSUB = "sumsub",
  ONMETA = "onmeta",
}

export type InvoicingData = {
  company_name?: string;
  address: string;
  country: string;
  city: string;
  reg_number: string;
  tax_id: string;
  postal_code: string;
};

export interface UpdatePhonePayload {
  phone: string;
  channel: PhoneChannel;
}

export interface Token {
  token?: string;
}

export interface Email {
  email: string;
}

export interface ChangeEmailPayload {
  new_email: string;
  current_password: string;
  code?: string;
}

export interface ChangeEmailTokenPayload {
  token: string;
  new_email: string;
  expires_at: string;
}

export interface ChangePasswordPayload {
  current_password: string;
  new_password: string;
  code?: string;
}

export interface User {
  id: string;
  account_type: AccountType;
  email: string;
  phone: string;
  phone_channel: PhoneChannel;
  phone_country: string;
  session_country: string;
  first_name: string;
  last_name: string;
  contact_name?: string;
  invoicing_data?: InvoicingData;
  is_2fa_enabled: boolean;
  kyc_complete: boolean;
  kyc_complete_and_approved: boolean;
  kyc_second_level_complete: boolean;
  kyc_second_level_complete_and_approved: boolean;
  kyc_second_level_count_threshold: number;
  kyc_second_level_amount_threshold: number;
  requires_first_level_kyc: boolean;
  kyc_country?: string;
  kyc_first_level_amount_threshold: number;
  marketing_data: MarketingData;
  intercom_user_hash?: string;
  ga_id?: string;
  allowed_incoming_currencies: Currency[];
  allowed_outgoing_currencies: Currency[];
  allowed_outgoing_chains?: Record<Currency, Chain[]>;
  outgoing_currencies_for_incoming_currency?: Record<Currency, Currency[]>;
  is_onmeta_eligible: boolean;
  onmeta_kyc_required: boolean;
  onmeta_kyc_complete: boolean;
  onmeta_kyc_complete_and_approved: boolean;
  is_usd_eligible: boolean;
  usd_kyc_complete: boolean;
  usd_kyc_complete_and_approved: boolean;
  usd_tos_accepted: boolean;
  primary_kyc_provider: KycProvider;
}

export interface ChangeUserNamePayload {
  first_name: string;
  last_name: string;
}

export interface Login {
  email: string;
  password: string;
  code?: string;
  contact_link_id?: string;
}

export interface ResetConfirm {
  uid?: string;
  token?: string;
  new_password: string;
}

export interface SignUpPayload {
  body: SignUpBody;
  query?: string;
}

export interface SignUpBody {
  account_type: AccountType;
  company_name: string;
  first_name: string;
  last_name: string;
  email: string;
  password: string;
  contact_link_id?: string;
}

export interface SignUpResponse {
  first_name: string;
  last_name: string;
  email: string;
  token: string;
}

export interface ActivationPayload {
  uid?: string;
  token?: string;
}

export interface AnonKYCRequest {
  jwt: string;
}

export interface TfaResponse {
  secret_key: string;
  provisioning_uri: string;
}

export interface EnableTfaPayload {
  code?: string;
}

export interface KycResponse {
  token?: string;
  url?: string;
}

export interface OnmetaKycResponse {
  onmeta_kyc_url?: string;
}

export interface USDKycResponse {
  usd_kyc_url?: string;
  usd_tos_url?: string;
}

export enum KYCStatus {
  NONE,
  STARTED,
  VERIFIED,
  SUSPECTED,
}
